// src/components/BarcodeScannerReal.js - Scanner avec vraie détection de codes-barres
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, AlertTriangle, Smartphone, X, Zap } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';

/**
 * Scanner de codes-barres avec détection réelle (html5-qrcode)
 */
const BarcodeScannerReal = ({ isOpen, onClose, onBarcodeDetected }) => {
  const [scannerMode, setScannerMode] = useState('camera');
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState('');
  const [scanCount, setScanCount] = useState(0);

  const html5QrCodeRef = useRef(null);
  const scannerIdRef = useRef('barcode-scanner-region');

  /**
   * Callback quand un code-barres est détecté
   */
  const onScanSuccess = useCallback(async (decodedText) => {
    // Nettoyer le code-barres (enlever espaces, retours à la ligne, etc.)
    const cleanBarcode = decodedText.trim().replace(/\s/g, '');

    // Validation : doit être numérique et de longueur 8-13
    if (!/^\d{8,13}$/.test(cleanBarcode)) {
      console.warn('⚠️ Code-barres invalide détecté:', {
        raw: JSON.stringify(decodedText),
        cleaned: cleanBarcode,
        length: cleanBarcode.length,
        isNumeric: /^\d+$/.test(cleanBarcode)
      });
      return; // Ne pas envoyer de code invalide
    }

    if (cleanBarcode === lastScan) return;

    console.log('✅ Code-barres valide détecté:', cleanBarcode);

    setLastScan(cleanBarcode);
    setScanCount(prev => prev + 1);

    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }

    try {
      if (html5QrCodeRef.current) {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) {
          await html5QrCodeRef.current.stop();
        }
      }
    } catch (err) {
      console.warn('⚠️ Erreur arrêt après détection:', err.message);
    }

    onBarcodeDetected(cleanBarcode);
    onClose();
  }, [lastScan, onBarcodeDetected, onClose]);

  const onScanError = useCallback(() => {
    // Erreurs silencieuses - normales quand pas de code dans la vue
  }, []);

  /**
   * Démarrer le scanner caméra
   */
  const startScanner = useCallback(async () => {
    setError(null);
    setIsScanning(true);
    setLastScan('');
    setScanCount(0);

    try {
      const scannerElement = document.getElementById(scannerIdRef.current);
      if (!scannerElement) {
        throw new Error('Élément scanner non trouvé dans le DOM');
      }

      // Toujours créer une instance fraîche pour éviter les problèmes d'état
      if (html5QrCodeRef.current) {
        try {
          const state = html5QrCodeRef.current.getState();
          if (state === 2) await html5QrCodeRef.current.stop();
        } catch (_) {}
        html5QrCodeRef.current = null;
      }

      html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);

      // Configuration optimisée pour les produits du commerce (canettes, bouteilles, boîtes)
      const config = {
        fps: 25, // FPS élevé pour mieux capturer les codes sur surfaces courbées
        qrbox: function(viewfinderWidth, viewfinderHeight) {
          // Zone large : 90% de la largeur, 45% de la hauteur
          // Idéal pour les codes linéaires (EAN-13) quelle que soit la taille du produit
          return {
            width: Math.floor(viewfinderWidth * 0.9),
            height: Math.floor(viewfinderHeight * 0.45)
          };
        },
        // Pas d'aspectRatio forcé : laisse la caméra dans son mode naturel
        formatsToSupport: [
          0,  // QR_CODE
          8,  // EAN_13  ← principal format produits alimentaires
          9,  // EAN_8   ← petits produits (bonbons, etc.)
          13, // UPC_A   ← produits américains
          14, // UPC_E   ← UPC compressé
          7,  // CODE_128 ← produits industriels
          3,  // CODE_39
          10, // ITF
        ],
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true  // Utilise l'API native si disponible (+ rapide)
        },
        showTorchButtonIfSupported: true, // Torche pour les environnements sombres
        rememberLastUsedCamera: true
      };

      let started = false;
      // Configuration simple qui fonctionne sur tous les appareils
      const cameraConfigs = [
        { facingMode: 'environment' },
        { facingMode: 'user' },
        {}
      ];

      for (const cameraConfig of cameraConfigs) {
        try {
          await html5QrCodeRef.current.start(
            cameraConfig,
            config,
            onScanSuccess,
            onScanError
          );
          started = true;
          setIsScanning(true);
          break;
        } catch (err) {
          console.warn('⚠️ Config caméra échouée:', err.message);
        }
      }

      if (!started) {
        throw new Error('Impossible de démarrer avec aucune configuration caméra');
      }

    } catch (err) {
      let errorMessage = 'Erreur lors du démarrage du scanner';
      let suggestion = '';

      if (err.name === 'NotAllowedError' || err.message.includes('Permission')) {
        errorMessage = 'Permission caméra refusée';
        suggestion = 'Autorisez l\'accès à la caméra dans les paramètres de votre navigateur.';
      } else if (err.name === 'NotFoundError' || err.message.includes('No Cameras')) {
        errorMessage = 'Aucune caméra trouvée';
        suggestion = 'Votre appareil n\'a pas de caméra accessible. Utilisez le mode manuel.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Caméra occupée';
        suggestion = 'Fermez les autres applications utilisant la caméra.';
      } else if (err.message) {
        errorMessage = err.message;
        suggestion = 'Essayez le mode manuel ou vérifiez votre navigateur.';
      }

      setError(`${errorMessage}. ${suggestion}`);
      setIsScanning(false);
    }
  }, [onScanSuccess, onScanError]);

  /**
   * Arrêter le scanner
   */
  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) {
          await html5QrCodeRef.current.stop();
        }
      } catch (err) {
        console.warn('⚠️ Erreur arrêt scanner:', err.message);
      }
    }
    setIsScanning(false);
  }, []);

  /**
   * Cleanup au démontage du composant
   */
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  /**
   * CORRECTION AUTO-START : réinitialisation complète à la fermeture
   * On efface aussi la ref pour forcer une instance fraîche à la prochaine ouverture
   */
  useEffect(() => {
    if (!isOpen) {
      stopScanner().then(() => {
        html5QrCodeRef.current = null; // ← clé du fix : instance fraîche à chaque ouverture
      });
      setError(null);
      setManualInput('');
      setScannerMode('camera');
      setLastScan('');
      setScanCount(0);
    }
  }, [isOpen, stopScanner]);

  /**
   * CORRECTION AUTO-START : déclenchement sur isOpen ET scannerMode
   * Quand isOpen passe à true avec mode camera → démarre directement
   */
  useEffect(() => {
    if (isOpen && scannerMode === 'camera' && !isScanning) {
      const timer = setTimeout(() => {
        startScanner();
      }, 500);
      return () => clearTimeout(timer);
    }
    if (scannerMode === 'manual' && isScanning) {
      stopScanner();
    }
  }, [isOpen, scannerMode, isScanning, startScanner, stopScanner]);

  /**
   * Saisie manuelle
   */
  const handleManualSubmit = useCallback(() => {
    const barcode = manualInput.trim();
    if (barcode && /^\d{8,13}$/.test(barcode)) {
      onBarcodeDetected(barcode);
      setManualInput('');
      onClose();
    } else {
      setError('Code-barres invalide (8-13 chiffres uniquement).');
    }
  }, [manualInput, onBarcodeDetected, onClose]);

  const testCodes = [
    { code: '3017624010701', name: 'Nutella 400g', brand: 'Ferrero' },
    { code: '8000500310427', name: 'Spaghetti N°5', brand: 'Barilla' },
    { code: '3228020000013', name: 'Yaourt Activia', brand: 'Danone' },
    { code: '5000169005484', name: 'Kit Kat', brand: 'Nestlé' },
    { code: '3274080005003', name: 'Président Beurre', brand: 'Lactalis' }
  ];

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="🔍 Scanner de Codes-Barres"
      size="lg"
    >
      <div className="space-y-4">
        {/* Sélecteur de mode */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          <Button
            onClick={() => setScannerMode('camera')}
            variant={scannerMode === 'camera' ? 'primary' : 'ghost'}
            size="sm"
            className="flex-1"
          >
            <Camera className="w-4 h-4" />
            Scanner Caméra
          </Button>
          <Button
            onClick={() => setScannerMode('manual')}
            variant={scannerMode === 'manual' ? 'primary' : 'ghost'}
            size="sm"
            className="flex-1"
          >
            <Smartphone className="w-4 h-4" />
            Saisie manuelle
          </Button>
        </div>

        {/* ─── MODE CAMÉRA ─── */}
        {scannerMode === 'camera' && (
          <div className="space-y-3">
            {/* Zone de scan */}
            <div className="relative">
              <div
                id={scannerIdRef.current}
                className="w-full rounded-lg overflow-hidden bg-black"
                style={{ minHeight: '300px' }}
              />

              {isScanning && (
                <div className="absolute top-3 right-3">
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2">
                    <Zap className="w-3 h-3 animate-pulse" />
                    Scan actif ({scanCount})
                  </div>
                </div>
              )}

              {/* Overlay de chargement avant que la caméra démarre */}
              {!isScanning && !error && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 bg-opacity-70 rounded-lg">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mb-3" />
                  <p className="text-white text-sm">Démarrage de la caméra...</p>
                </div>
              )}
            </div>

            {/* Erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700 font-medium">Scanner non disponible</p>
                    <p className="text-xs text-red-600 mt-1">{error}</p>
                    <div className="mt-3 p-2 bg-white rounded border border-red-200">
                      <p className="text-xs text-gray-700 font-medium mb-1">Diagnostic :</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>• HTTPS : {window.location.protocol === 'https:' || window.location.hostname === 'localhost' ? '✅ Oui' : '❌ Non (requis)'}</li>
                        <li>• Caméra API : {navigator.mediaDevices ? '✅ Supporté' : '❌ Non supporté'}</li>
                      </ul>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button onClick={startScanner} variant="outline" size="sm" className="text-blue-600 border-blue-300">
                        🔄 Réessayer
                      </Button>
                      <Button onClick={() => setScannerMode('manual')} variant="outline" size="sm" className="text-orange-600 border-orange-300">
                        📝 Mode manuel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conseils de scan */}
            {!error && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-xs font-medium text-amber-800 mb-1">Conseils pour un bon scan</p>
                <ul className="text-xs text-amber-700 space-y-0.5">
                  <li>• Distance idéale : <strong>15–25 cm</strong> — téléphone récent/haut de gamme (S23, iPhone 15…) : <strong>reculez à 25–35 cm</strong></li>
                  <li>• Centrez le code-barres dans la <strong>zone claire</strong></li>
                  <li>• Bouteille/canette : tournez pour <strong>aplatir</strong> le code face à la caméra</li>
                  <li>• Si flou : <strong>éloignez</strong> puis restabilisez — le grand capteur a une mise au point minimale plus lointaine</li>
                  <li>• Éclairage insuffisant : utilisez le <strong>bouton torche</strong> (si disponible)</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ─── MODE MANUEL ─── */}
        {scannerMode === 'manual' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Saisissez le code-barres (8–13 chiffres)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => {
                    setManualInput(e.target.value.replace(/\D/g, ''));
                    setError(null);
                  }}
                  placeholder="Ex: 3017624010701"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-mono text-center"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit()}
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="13"
                />
                <Button
                  onClick={handleManualSubmit}
                  disabled={!manualInput.trim() || manualInput.length < 8}
                  variant="primary"
                  size="lg"
                >
                  Rechercher
                </Button>
              </div>
              {manualInput && manualInput.length < 8 && (
                <p className="text-sm text-orange-600 mt-1">
                  Code trop court ({manualInput.length}/8 min)
                </p>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3">Codes pour test</h4>
              <div className="grid grid-cols-1 gap-2">
                {testCodes.map((item) => (
                  <div key={item.code} className="flex items-center justify-between p-3 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
                    <div className="flex-1 cursor-pointer" onClick={() => setManualInput(item.code)}>
                      <div className="font-mono text-sm font-medium text-blue-600">{item.code}</div>
                      <div className="text-xs text-gray-600">{item.name} — {item.brand}</div>
                    </div>
                    <Button
                      onClick={() => {
                        setManualInput(item.code);
                        setTimeout(handleManualSubmit, 100);
                      }}
                      variant="success"
                      size="sm"
                      className="ml-2"
                    >
                      Rechercher
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Détection réelle EAN-13 · UPC · Code-128
        </div>
        <Button onClick={onClose} variant="secondary">
          <X className="w-4 h-4" />
          Fermer
        </Button>
      </div>
    </Modal>
  );
};

export default BarcodeScannerReal;
